import { ref } from 'vue'
import { defineStore } from 'pinia'
import {
  doc,
  getDoc,
  setDoc,
  collection,
  getDocs,
  writeBatch,
  serverTimestamp,
  onSnapshot,
  type Unsubscribe,
} from 'firebase/firestore'
import { db } from '@/firebase'
import { useSyncStore } from './sync'
import { useAuthStore } from './auth'
import { writeAuditLog } from '@/services/audit'
import type { IAPData } from '@/types'

export const useIAPStore = defineStore('iap', () => {
  const iap = ref<IAPData | null>(null)
  const loading = ref(false)
  const exporting = ref(false)

  let unsubFn: Unsubscribe | null = null
  let _incidentId = ''
  let _opId = ''

  function subscribeToIAP(incidentId: string, opId: string) {
    if (incidentId === _incidentId && opId === _opId && unsubFn) return
    if (unsubFn) unsubFn()
    _incidentId = incidentId
    _opId = opId
    loading.value = true
    unsubFn = onSnapshot(
      doc(db, 'incidents', incidentId, 'operational_periods', opId, 'iap', 'data'),
      (snap) => {
        iap.value = snap.exists() ? (snap.data() as IAPData) : null
        loading.value = false
      },
    )
  }

  async function initializeIAP(incidentId: string, opId: string) {
    const iapRef = doc(db, 'incidents', incidentId, 'operational_periods', opId, 'iap', 'data')
    const existing = await getDoc(iapRef)
    if (existing.exists()) return

    await setDoc(iapRef, {
      status: 'not_started',
      prepared_by_202: '',
      prepared_by_203: '',
      prepared_by_205: '',
      prepared_by_206: '',
      prepared_by_208: '',
      created_at: serverTimestamp(),
    })

    // Find prior OP and copy medical_plan + hazards
    const opsSnap = await getDocs(
      collection(db, 'incidents', incidentId, 'operational_periods'),
    )
    const ops = opsSnap.docs
      .map((d) => ({ op_id: d.id, op_number: d.data().op_number as number }))
      .sort((a, b) => a.op_number - b.op_number)
    const currentIdx = ops.findIndex((o) => o.op_id === opId)
    const priorOp = currentIdx > 0 ? ops[currentIdx - 1] : null

    if (priorOp) {
      const batch = writeBatch(db)

      const priorMedRef = doc(
        db,
        'incidents',
        incidentId,
        'operational_periods',
        priorOp.op_id,
        'medical_plan',
        'data',
      )
      const priorMed = await getDoc(priorMedRef)
      if (priorMed.exists()) {
        batch.set(
          doc(db, 'incidents', incidentId, 'operational_periods', opId, 'medical_plan', 'data'),
          priorMed.data(),
        )
      }

      const priorHazSnap = await getDocs(
        collection(db, 'incidents', incidentId, 'operational_periods', priorOp.op_id, 'hazards'),
      )
      priorHazSnap.docs.forEach((h) => {
        const newRef = doc(
          collection(db, 'incidents', incidentId, 'operational_periods', opId, 'hazards'),
        )
        batch.set(newRef, h.data())
      })

      await batch.commit()
    }

    await writeAuditLog(incidentId, 'iap.initialized', 'iap', opId, null, { op_id: opId })
  }

  async function savePreparedBy(
    incidentId: string,
    opId: string,
    section: '202' | '203' | '205' | '206' | '208',
    name: string,
  ) {
    const syncStore = useSyncStore()
    syncStore.pendingWrites++
    try {
      await setDoc(
        doc(db, 'incidents', incidentId, 'operational_periods', opId, 'iap', 'data'),
        { [`prepared_by_${section}`]: name },
        { merge: true },
      )
      await setStatus(incidentId, opId, 'draft')
    } finally {
      syncStore.pendingWrites--
    }
  }

  async function setStatus(incidentId: string, opId: string, status: 'draft') {
    if (iap.value?.status === status) return
    await setDoc(
      doc(db, 'incidents', incidentId, 'operational_periods', opId, 'iap', 'data'),
      { status },
      { merge: true },
    )
  }

  async function exportIAP(incidentId: string, opId: string): Promise<string> {
    const authStore = useAuthStore()
    exporting.value = true
    try {
      const token = await authStore.currentUser?.getIdToken()
      const res = await fetch(
        `/api/incidents/${incidentId}/operational_periods/${opId}/iap/export-pdf`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        },
      )
      const body = await res.json()
      if (!res.ok || body.error) throw new Error(body.error?.message ?? 'Export failed')
      return body.data.pdf_url as string
    } finally {
      exporting.value = false
    }
  }

  function teardown() {
    if (unsubFn) { unsubFn(); unsubFn = null }
    _incidentId = ''
    _opId = ''
    iap.value = null
    loading.value = false
  }

  return { iap, loading, exporting, subscribeToIAP, initializeIAP, savePreparedBy, setStatus, exportIAP, teardown }
})
